import { Button } from '@/components/ui/button';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useMsal } from '@azure/msal-react';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: 'Admin' | 'Manager' | 'User';
  requireAnyRole?: ('Admin' | 'Manager' | 'User')[];
}

/**
 * Protected route component that enforces authentication and authorization
 * Redirects to /login if not authenticated
 * Redirects to /unauthorized if user lacks required role
 */
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requiredRole,
  requireAnyRole
}) => {
  const { user, isLoading } = useAuth();
  const { accounts } = useMsal();

  // Redirect to login if no MSAL account exists
  if (accounts.length === 0) {
    return <Navigate to="/login" replace state={{ from: window.location.pathname }} />;
  }

  // Show loading state while user data is being fetched
  if (isLoading || !user) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="mx-auto h-10 w-10 animate-spin rounded-full border-2 border-border border-t-primary" role="status" aria-label="Loading"></div>
          <p className="mt-4 text-sm text-muted-foreground">Loading…</p>
        </div>
      </div>
    );
  }

  // Check role-based authorization if required
  if (requiredRole || requireAnyRole) {
    const roleHierarchy: Record<string, number> = { 
      Admin: 3, 
      Manager: 2, 
      User: 1 
    };
    
    const userLevel = roleHierarchy[user?.role as keyof typeof roleHierarchy] || 0;

    // Check single required role (hierarchical)
    if (requiredRole) {
      const requiredLevel = roleHierarchy[requiredRole];
      
      if (userLevel < requiredLevel) {
        return <Navigate to="/unauthorized" replace />;
      }
    }

    // Check if user has any of the required roles
    if (requireAnyRole) {
      const hasRequiredRole = requireAnyRole.some(role => {
        const requiredLevel = roleHierarchy[role];
        return userLevel >= requiredLevel;
      });

      if (!hasRequiredRole) {
        return <Navigate to="/unauthorized" replace />;
      }
    }
  }

  // User is authenticated and authorized
  return <>{children}</>;
};

/**
 * Unauthorized page component
 */
export const UnauthorizedPage: React.FC = () => {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background px-4 text-center">
      <img src="/deheus-primary-logo.svg" alt="De Heus" className="h-14 w-14" />
      <h1 className="mt-6 text-2xl font-semibold tracking-tight">Access denied</h1>
      <p className="mt-1 max-w-sm text-sm text-muted-foreground">
        You do not have permission to open this page. Contact your administrator if you think this is a mistake.
      </p>
      <Button onClick={() => window.history.back()} className="mt-6">
        Go back
      </Button>
    </div>
  );
};
